import React, { useEffect, useState, useCallback } from 'react'
import { Table, Button, Input, Select, Tag, Space, message, Typography } from 'antd'
import { PlusOutlined, EyeOutlined, EditOutlined, DeleteOutlined } from '@ant-design/icons'
import { useNavigate } from 'react-router-dom'
import { purchaseApi } from '../../services/api'

const { Title } = Typography

const STATUS_COLORS = {
  '草稿': 'default',
  '审批中': 'processing',
  '已驳回': 'error',
  '已下单': 'blue',
  '已到货': 'cyan',
  '已完成': 'green',
}

const SOURCE_TAGS = {
  '合同需求': 'blue',
  '库存预警': 'orange',
  '项目需求': 'purple',
  '手工申请': 'green',
}

export default function Purchases() {
  const navigate = useNavigate()
  const [data, setData] = useState([])
  const [total, setTotal] = useState(0)
  const [loading, setLoading] = useState(false)
  const [query, setQuery] = useState({ page: 1, pageSize: 20, keyword: '', status: '' })

  const fetchData = useCallback(async () => {
    setLoading(true)
    try {
      const res = await purchaseApi.list(query)
      setData(res.data)
      setTotal(res.total)
    } catch {
      message.error('加载采购列表失败')
    } finally {
      setLoading(false)
    }
  }, [query])

  useEffect(() => {
    fetchData()
  }, [fetchData])

  const handleDelete = async (id) => {
    try {
      await purchaseApi.delete(id)
      message.success('已删除')
      fetchData()
    } catch (e) {
      message.error(e?.error || '删除失败')
    }
  }

  const columns = [
    { title: '采购编号', dataIndex: 'code', key: 'code', width: 160 },
    {
      title: '供应商', key: 'supplier', width: 140,
      render: (_, r) => r.supplier?.name || '—',
    },
    {
      title: '来源', dataIndex: 'source', key: 'source', width: 100,
      render: (v) => <Tag color={SOURCE_TAGS[v] || 'default'}>{v}</Tag>,
    },
    {
      title: '状态', dataIndex: 'status', key: 'status', width: 90,
      render: (v) => <Tag color={STATUS_COLORS[v] || 'default'}>{v}</Tag>,
    },
    {
      title: '金额', dataIndex: 'totalAmount', key: 'totalAmount', width: 120,
      render: (v) => `¥${Number(v).toLocaleString()}`,
    },
    {
      title: '明细', key: 'items', width: 60,
      render: (_, r) => r._count?.items || 0,
    },
    {
      title: '申请人', key: 'user', width: 80,
      render: (_, r) => r.user?.name || '—',
    },
    {
      title: '创建时间', dataIndex: 'createdAt', key: 'createdAt', width: 120,
      render: (v) => v ? new Date(v).toLocaleDateString('zh-CN') : '—',
    },
    {
      title: '操作', key: 'actions', width: 200,
      render: (_, r) => (
        <Space>
          <Button size="small" icon={<EyeOutlined />} onClick={() => navigate(`/purchases/${r.id}`)}>详情</Button>
          {(r.status === '草稿' || r.status === '已驳回') && (
            <Button size="small" icon={<EditOutlined />} onClick={() => navigate(`/purchases/new?id=${r.id}`)}>编辑</Button>
          )}
          {r.status === '草稿' && (
            <Button size="small" danger icon={<DeleteOutlined />} onClick={() => handleDelete(r.id)}>删除</Button>
          )}
        </Space>
      ),
    },
  ]

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
        <Title level={4} style={{ margin: 0 }}>采购管理</Title>
        <Button type="primary" icon={<PlusOutlined />} onClick={() => navigate('/purchases/new')}>新建采购申请</Button>
      </div>

      <Space style={{ marginBottom: 16 }}>
        <Input.Search
          placeholder="搜索编号/来源/供应商"
          allowClear
          style={{ width: 260 }}
          onSearch={(v) => setQuery((q) => ({ ...q, keyword: v, page: 1 }))}
        />
        <Select
          placeholder="筛选状态"
          allowClear
          style={{ width: 140 }}
          onChange={(v) => setQuery((q) => ({ ...q, status: v || '', page: 1 }))}
        >
          {['草稿', '审批中', '已驳回', '已下单', '已到货', '已完成'].map((s) => (
            <Select.Option key={s} value={s}>{s}</Select.Option>
          ))}
        </Select>
      </Space>

      <Table
        columns={columns}
        dataSource={data}
        rowKey="id"
        loading={loading}
        pagination={{
          total,
          current: query.page,
          pageSize: query.pageSize,
          showTotal: (t) => `共 ${t} 条采购申请`,
          onChange: (page) => setQuery((q) => ({ ...q, page })),
        }}
      />
    </div>
  )
}
